<template>
  <div class="flex items-center gap-2 w-full ">
    <select
      v-model="selectedLocation"
      class="w-full bg-gray-900 border border-gray-700 px-3 py-2 focus:border-green-500 outline-none text-white shadow-inner"
    >
      <option v-for="loc in locations" :key="loc" :value="loc">
        {{ loc }}
        <span v-if="availableCount[loc] !== undefined && isAdmin" class="text-gray-400">
          (ว่าง {{ availableCount[loc] }})
        </span>
      </option>
    </select>
  </div>

  <div class="bg pb-24 mb-auto h-full overflow-auto bg-gray-800" id="style-1">
    <div class="max-w-full mx-auto space-y-5 text-white">
      <!-- Error -->
      <div v-if="error" class="rounded-xl border border-red-700 bg-red-900 bg-opacity-30 p-4 text-sm text-red-200">
        <div class="flex items-start gap-3">
          <i class="fa-solid fa-triangle-exclamation text-red-300 text-lg"></i>
          <div>
            <p class="font-medium">{{ errorMessage }}</p>
            <p v-if="indexLink" class="mt-1">
              <a :href="indexLink" target="_blank" class="underline hover:no-underline">สร้างดัชนี (index)</a>
            </p>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-sm text-gray-300">กำลังโหลดข้อมูล…</div>

      <!-- Empty -->
      <div v-else-if="filteredOutposts.length === 0" class="border-gray-700 bg-gray-900 bg-opacity-80 p-8 text-center">
        <i class="fa-solid fa-tower-observation text-gray-400 text-3xl mb-2"></i>
        <p v-if="filterMode === 'owned'">ยังไม่มี Outpost ที่มีเจ้าของในพื้นที่นี้</p>
        <p v-else-if="filterMode === 'vacant'">ยังไม่มี Outpost ที่ว่างในพื้นที่นี้</p>
        <p v-else>ยังไม่มี Outpost ในพื้นที่นี้</p>
      </div>

      <!-- Filter tabs -->
      <div v-if="!loading && !error" class="flex gap-1 px-1 pt-2">
        <button
          v-for="f in filterOptions"
          :key="f.value"
          @click="filterMode = f.value"
          class="px-3 py-1 rounded-full text-xs font-medium border transition duration-200"
          :class="filterMode === f.value
            ? 'bg-green-700 border-green-500 text-white'
            : 'bg-gray-800 border-gray-600 text-gray-300 hover:border-gray-400'"
        >
          {{ f.label }}
          <span class="ml-1 opacity-70">({{ f.count }})</span>
        </button>
      </div>

      <table v-if="filteredOutposts.length > 0" class="min-w-full text-sm text-gray-200">
        <thead class="bg-gray-900 bg-opacity-90 text-gray-300">
          <tr>
            <th class="px-4 py-3 text-left w-14">-</th>
            <th class="px-4 py-3 text-left">Outpost</th>
            <th class="px-4 py-3 text-left">เลเวล</th>
            <th class="px-4 py-3 text-left">เพชรรายวัน</th>
            <th class="px-4 py-3 text-left">คะแนนยึดครอง</th>
            <th v-if="isAdmin" class="px-4 py-3 text-left">ถูกโจมตีล่าสุด</th>
            <th class="hidden md:table-cell px-4 py-3 text-left">ชาวบ้านป้องกันสูงสุด</th>
            <th class="hidden md:table-cell px-4 py-3 text-left">ชาวบ้านบุกสูงสุด</th>
            <th class="hidden md:table-cell px-4 py-3 text-left">NPC</th>
            <th class="px-4 py-3 text-left">เจ้าของ</th>
            <th class="px-3 py-3 text-right w-10"></th>
          </tr>
        </thead>

        <tbody class="divide-y divide-gray-800 bg-gray-800 bg-opacity-80">
          <tr
            v-for="row in filteredOutposts"
            :key="row._id"
            class="hover:bg-gray-700 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-500"
            :class="row.isClearCooldown ? 'opacity-60' : ''"
            @click="openModal(row._id)"
            tabindex="0"
            @keyup.enter="openModal(row._id)"
          >
            <td class="px-4 py-3">
              <i v-if="row.event" class="fas fa-fire text-red-400 animate-pulse" title="มีเหตุการณ์อยู่"></i>
              <!-- ✅ NEW: แสดง icon ฟื้นตัว พร้อม tooltip นับถอยหลัง -->
              <span
                v-else-if="row.isClearCooldown"
                class="inline-flex items-center gap-1 text-gray-500"
                :title="`ฟื้นตัวอยู่ เปิดให้ยึดได้อีก ${formatRemainingShort(row.clearedUntilMs)}`"
              >
                <i class="fas fa-shield-halved"></i>
              </span>
              <i v-else-if="!row.treasureOpened" class="fas fa-box text-green-300" title="กล่องสมบัติพร้อมเปิด"></i>
              <i v-else class="fas fa-tower-observation text-yellow-700"></i>
            </td>
            <td class="px-4 py-3">
              <div>{{ row.name }}</div>
              <!-- ✅ NEW: แสดง badge ฟื้นตัวใต้ชื่อ -->
              <div v-if="row.isClearCooldown" class="text-xxs text-gray-500 mt-0.5 flex items-center gap-1">
                <i class="fas fa-hourglass-half text-gray-600 text-xxs"></i>
                ฟื้นตัวอีก {{ formatRemainingShort(row.clearedUntilMs) }}
              </div>
            </td>
            <td class="px-4 py-3 font-semibold text-green-300">{{ row.level }}</td>
            <td class="px-4 py-3">{{ row.dailyDiamondReward }}</td>
            <td class="px-4 py-3">{{ row.occupationScore }}</td>
            <td v-if="isAdmin" class="px-4 py-3">
              {{ formatTimeAgo(row.lastAttackedAt) }}
            </td>
            <td class="hidden md:table-cell px-4 py-3">{{ row.maxDefenderVillagers }}</td>
            <td class="hidden md:table-cell px-4 py-3">{{ row.maxAttackerVillagers }}</td>
            <td class="hidden md:table-cell px-4 py-3">{{ row.defenderCount }}</td>
            <td class="px-4 py-3">
              <div v-if="row.ownerUid" class="flex items-center gap-2">
                <img
                  v-if="row.characterImageUrl"
                  :src="row.characterImageUrl"
                  class="block w-5 h-5 rounded-full object-cover"
                  alt="Character Image"
                />
                {{ row.characterName }}
              </div>
              <span v-else class="text-gray-500 text-xs italic">ไม่มีเจ้าของ</span>
            </td>
            <td class="px-3 py-3 text-right">
              <button
                v-if="row.ownerUid === user?.uid"
                class="p-2 rounded-lg hover:bg-gray-800 border border-transparent hover:border-gray-700 transition"
                aria-label="Manage outpost"
                @click.stop="openManage(row._id)"
              >
                <i class="fa-solid fa-gear text-gray-300 text-sm"></i>
              </button>
              <button
                v-if="isAdmin"
                class="p-2 rounded-lg hover:bg-gray-800 border border-transparent hover:border-gray-700 transition"
                aria-label="Edit outpost"
                @click.stop="openEdit(row._id)"
              >
                <i class="fa-solid fa-pen-to-square text-gray-300 text-sm"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- View Modal -->
      <OutpostModal
        v-if="modalOpen"
        :key="activeOutpostId"
        :outpost-id="activeOutpostId"
        @close="closeModal"
      />

      <!-- Manage Modal (owner only) -->
      <ManageOutpostModal
        v-if="manageOpen"
        :outpost-id="activeOutpostId"
        @close="closeManage"
      />

      <!-- Edit Modal (admin only) -->
      <EditOutpostModal
        v-if="editOpen && isAdmin"
        :outpost-id="activeOutpostId"
        @close="closeEdit"
      />
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch, onBeforeUnmount, computed } from 'vue'
import { projectFirestore } from '@/firebase/config'
import OutpostModal from '@/components/OutpostModal.vue'
import ManageOutpostModal from '@/components/ManageOutpostModal.vue'
import EditOutpostModal from '@/components/admin/EditOutpostModal.vue'
import getUser from '@/composables/getUser'
import { usePlayerStore } from '@/composables/usePlayerStore'

export default {
  components: { OutpostModal, ManageOutpostModal, EditOutpostModal },

  setup() {
    const LOCAL_KEY = 'selectedLocation'

    const locations = [
      'รถไฟฟ้าใต้ดิน',
      'โรงพยาบาล', 'หมู่บ้านคนรวย', 'สวนสนุก', 'โรงงานอุตสาหกรรม',
      'ห้างสรรพสินค้า',
      'สถานีดับเพลิงร้าง', 'ค่ายทหาร',
      'สถาบันวิจัยหุ่นยนต์', 'สถาบันวิจัยไวรัส', 'สุสานนักรบโบราณ', 'ตึกความมันคงแห่งชาติ',
      'โรงเก็บศพจักรกล', 'ตึกปฏิบัติการพันธุกรรม',
    ]

    const selectedLocation = ref(
      localStorage.getItem(LOCAL_KEY) && locations.includes(localStorage.getItem(LOCAL_KEY))
        ? localStorage.getItem(LOCAL_KEY)
        : locations[0]
    )

    const availableCount = ref({})
    const availableInCurrentLocation = ref(0)

    const loading = ref(false)
    const error = ref(null)
    const indexLink = ref('')
    const outposts = ref([])
    let unsubscribe = null

    const now = ref(Date.now())

    // ✅ NEW: filterMode แทน showOnlyOwned — 'all' | 'owned' | 'vacant'
    const filterMode = ref('all')

    const filterOptions = computed(() => [
      { value: 'all', label: 'ทั้งหมด', count: outposts.value.length },
      { value: 'owned', label: 'มีเจ้าของ', count: outposts.value.filter(o => o.ownerUid && o.ownerUid !== '').length },
      { value: 'vacant', label: 'ว่าง', count: outposts.value.filter(o => !o.ownerUid || o.ownerUid === '').length },
    ])

    // ✅ NEW: computed กรองตาม filterMode
    const filteredOutposts = computed(() => {
      if (filterMode.value === 'owned') {
        return outposts.value.filter(o => o.ownerUid && o.ownerUid !== '')
      }
      if (filterMode.value === 'vacant') {
        return outposts.value.filter(o => !o.ownerUid || o.ownerUid === '')
      }
      return outposts.value
    })

    // ดึงจำนวน Outpost ว่างของทุก location
    const fetchAvailableCounts = async () => {
      const promises = locations.map(async (loc) => {
        try {
          const queryNull = projectFirestore
            .collection('outposts')
            .where('location', '==', loc)
            .where('ownerUid', '==', null)

          const queryEmpty = projectFirestore
            .collection('outposts')
            .where('location', '==', loc)
            .where('ownerUid', '==', '')

          const [snapNull, snapEmpty] = await Promise.all([
            queryNull.get(),
            queryEmpty.get()
          ])

          const count = snapNull.size + snapEmpty.size
          availableCount.value[loc] = count

          if (loc === selectedLocation.value) {
            availableInCurrentLocation.value = count
          }
        } catch (err) {
          console.error(`Error counting available outposts for ${loc}:`, err)
          availableCount.value[loc] = '?'
        }
      })

      await Promise.all(promises)
    }

    const fetchData = () => {
      loading.value = true
      error.value = null
      indexLink.value = ''
      outposts.value = []

      if (unsubscribe) unsubscribe()

      const q = projectFirestore
        .collection('outposts')
        .where('location', '==', selectedLocation.value)
        .orderBy('createdAt', 'asc')
        .orderBy('level', 'desc')

      unsubscribe = q.onSnapshot(
        async (snap) => {
          const promises = snap.docs.map(async (doc) => {
            const data = doc.data()
            let characterName = '-'
            let characterImageUrl = null

            if (data.ownerUid) {
              try {
                const charDoc = await projectFirestore.collection('characters').doc(data.ownerUid).get()
                if (charDoc.exists) {
                  const charData = charDoc.data()
                  characterName = charData.characterName || data.ownerUid
                  characterImageUrl = charData.imageUrl || null
                } else {
                  characterName = data.ownerUid
                }
              } catch (e) {
                console.error('Fetch character error:', e)
                characterName = data.ownerUid
              }
            }

            // ✅ NEW: ดึงจำนวน player defenders จาก outpostPrivate
            let playerDefenderCount = 0
            if (data.ownerUid) {
              try {
                const privDoc = await projectFirestore.collection('outpostPrivate').doc(doc.id).get()
                if (privDoc.exists) {
                  playerDefenderCount = Object.keys(privDoc.data().defenders || {}).length
                }
              } catch (e) {
                console.error('Fetch outpostPrivate error:', e)
              }
            }

            // ✅ NEW: คำนวณ clearedUntilMs
            const clearedUntilMs = data.clearedUntil
              ? data.clearedUntil.toDate().getTime()
              : null
            const isClearCooldown = clearedUntilMs ? clearedUntilMs > now.value : false

            return {
              _id: doc.id,
              name: data.name,
              level: data.level ?? 0,
              ownerUid: data.ownerUid,
              imageUrl: data.imageUrl,
              dailyDiamondReward: data.dailyDiamondReward ?? 0,
              occupationScore: data.occupationScore ?? 0,
              maxDefenderVillagers: data.maxDefenderVillagers ?? 0,
              maxAttackerVillagers: data.maxAttackerVillagers ?? 0,
              defenderCount: (data.defenders ? data.defenders.length : 0) + playerDefenderCount,
              characterName,
              characterImageUrl,
              treasureOpened: data.treasureOpened ?? false,
              event: data.event ?? null,
              lastAttackedAt: data.lastAttackedAt,
              // ✅ NEW
              isClearCooldown,
              clearedUntilMs,
            }
          })

          const results = await Promise.all(promises)
          outposts.value = results
          loading.value = false
        },
        (err) => {
          loading.value = false
          error.value = err
          const match = String(err.message).match(/https?:\/\/[^\s)]+/g)
          if (match) indexLink.value = match[0]
        }
      )
    }

    const formatTimeAgo = (timestamp) => {
      if (!timestamp) return '-'
      const last = timestamp.toDate().getTime()
      const minutes = Math.floor((now.value - last) / 60000)
      if (minutes < 60) return `${minutes} นาทีที่แล้ว`
      const hours = Math.floor(minutes / 60)
      return `${hours} ชั่วโมงที่แล้ว`
    }

    // ✅ NEW: format เวลาที่เหลือแบบสั้น (สำหรับ table)
    const formatRemainingShort = (untilMs) => {
      if (!untilMs) return ''
      const ms = Math.max(0, untilMs - now.value)
      if (ms <= 0) return 'พร้อมแล้ว'
      const days = Math.floor(ms / 86400000)
      const hours = Math.floor((ms % 86400000) / 3600000)
      const minutes = Math.floor((ms % 3600000) / 60000)
      if (days > 0) return `${days}ว ${hours}ชม`
      if (hours > 0) return `${hours}ชม ${minutes}น`
      return `${minutes}น`
    }

    // ===== Admin check =====
    const { user } = getUser()
    const playerStore = usePlayerStore()
    const isAdmin = ref(false)

    watch(() => playerStore.userData, (data) => {
      if (data) {
        isAdmin.value = data.admin === true
      } else {
        isAdmin.value = false
      }
    }, { immediate: true })

    // ===== Modal controls =====
    const modalOpen = ref(false)
    const activeOutpostId = ref(null)
    const openModal = (id) => {
      activeOutpostId.value = id
      modalOpen.value = true
    }
    const closeModal = () => {
      modalOpen.value = false
      activeOutpostId.value = null
    }

    const manageOpen = ref(false)
    const openManage = (id) => {
      activeOutpostId.value = id
      manageOpen.value = true
    }
    const closeManage = () => {
      manageOpen.value = false
      activeOutpostId.value = null
    }

    const editOpen = ref(false)
    const openEdit = (id) => {
      activeOutpostId.value = id
      editOpen.value = true
    }
    const closeEdit = () => {
      editOpen.value = false
    }

    // Lifecycle & watchers
    onMounted(async () => {
      await fetchAvailableCounts()
      fetchData()
      if (user.value?.uid) {
        playerStore.init(user.value.uid)
      }

      const interval = setInterval(() => {
        now.value = Date.now()
      }, 60000)
      onBeforeUnmount(() => clearInterval(interval))
    })

    watch(selectedLocation, (val) => {
      localStorage.setItem(LOCAL_KEY, val)
      availableInCurrentLocation.value = availableCount.value[val] ?? 0
      fetchData()
    })

    onBeforeUnmount(() => {
      if (unsubscribe) unsubscribe()
    })

    const errorMessage = computed(() =>
      error.value?.code?.includes('failed-precondition')
        ? 'ต้องสร้างดัชนี (index) ก่อนใช้งานคิวรีนี้'
        : error.value?.message || ''
    )

    return {
      locations,
      selectedLocation,
      availableCount,
      availableInCurrentLocation,
      loading,
      error,
      errorMessage,
      indexLink,
      outposts,
      filteredOutposts,
      filterMode,
      filterOptions,
      now,
      formatTimeAgo,
      formatRemainingShort,
      isAdmin,
      user,
      modalOpen,
      activeOutpostId,
      openModal,
      closeModal,
      manageOpen,
      openManage,
      closeManage,
      editOpen,
      openEdit,
      closeEdit
    }
  }
}
</script>

<style scoped>
.bg {
  background:
    url('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/bg%2Foutpost.png?alt=media&token=d01e6d11-b702-4fa8-8901-de994ecd1060')
    no-repeat center center fixed,
    linear-gradient(to bottom, rgba(31, 41, 55, 0.8), rgba(0, 0, 0, 0.9));
}
table th,
table td {
  white-space: nowrap;
}
.text-xxs {
  font-size: 0.68rem;
  line-height: 1rem;
}
</style>